'use client';
import { MediaType } from '@repo/ui/src/types/image';
import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import useEmblaCarousel from 'embla-carousel-react';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import Image from '@repo/ui/src/components/Image/New';
import { classNames } from '@repo/ui/src/utils/classNames';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';
import { useIsLG } from '@repo/ui/src/utils/useMediaQuery';

interface MeaterPDPHeroCarouselProps {
  images: Array<MediaType>;
}

const MeaterPDPHeroCarousel = ({ images = [] }: MeaterPDPHeroCarouselProps) => {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const isDesktop = useIsLG();

  // Start with horizontal (mobile-first); reInit flips to vertical once mounted on desktop.
  const [thumbsRef, thumbsApi] = useEmblaCarousel({
    axis: 'x',
    dragFree: true,
    align: 'start',
    containScroll: 'trimSnaps',
  });

  // Reinitialize Embla axis when the md breakpoint changes.
  React.useEffect(() => {
    thumbsApi?.reInit({
      axis: isDesktop ? 'y' : 'x',
      dragFree: true,
      align: 'start',
      containScroll: 'trimSnaps',
    });
  }, [thumbsApi, isDesktop]);

  const update = () => {
    setCanScrollPrev(thumbsApi.canScrollPrev());
    setCanScrollNext(thumbsApi.canScrollNext());
  };

  // Track whether the thumbnail strip can scroll in either direction.
  React.useEffect(() => {
    if (!thumbsApi) return;

    thumbsApi.on('scroll', update);
    thumbsApi.on('reInit', update);
    thumbsApi.on('slideFocus', update);
    update();
    return () => {
      thumbsApi.off('scroll', update);
      thumbsApi.off('reInit', update);
    };
  }, [thumbsApi]);

  // Scroll the active thumbnail into view when the main image changes.
  React.useEffect(() => {
    thumbsApi?.scrollTo(currentImageIndex);
  }, [thumbsApi, currentImageIndex]);

  const activeImage = images[currentImageIndex];

  return (
    <div className="w-full min-w-0">
      <h2 className="sr-only">
        {t('productDetailPageContent.imageGalleryText', undefined, { default: 'Image Gallery' })}:{' '}
        {images.length} {t('genericContent.imagesText', undefined, { default: 'Images' })}
      </h2>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
        <div
          className={classNames(
            'relative lg:w-[94px] overflow-hidden',
            'md:h-[94px] lg:h-auto',
            'order-2 lg:order-1',
          )}
        >
          <div
            className={classNames(
              'md:absolute inset-0',
              'lg:shrink-0 mx-4 sm:mx-10 md:mx-0 max-w-[90vw]',
              'flex flex-row lg:flex-col items-center gap-1',
            )}
          >
            {(canScrollPrev || canScrollNext) && (
              <button
                onClick={() => thumbsApi?.scrollPrev()}
                aria-label={t('productDetailPageContent.previousImagesText', undefined, {
                  default: 'Previous images',
                })}
                tabIndex={canScrollPrev ? undefined : -1}
                className={classNames(
                  'shrink-0 flex items-center justify-center w-6 h-6 text-neutral-600 hover:text-neutral-900 border-2 border-neutral-300 transition-opacity duration-200',
                  !canScrollPrev && 'opacity-0 pointer-events-none',
                )}
              >
                <ChevronIcon direction={isDesktop ? 'up' : 'left'} className="w-4 h-4" />
              </button>
            )}

            <div ref={thumbsRef} className="flex-1 min-w-0 overflow-hidden py-2 md:p-1">
              <div className="flex flex-row lg:flex-col gap-2 lg:pb-1 h-full">
                {images.map((image, i) => (
                  <button
                    key={i}
                    aria-label={`${t('productDetailPageContent.imageThumbnailText', undefined, { default: 'Image' })} ${i + 1}`}
                    aria-current={currentImageIndex === i}
                    onClick={() => setCurrentImageIndex(i)}
                    className={classNames(
                      'shrink-0 w-[72px] lg:w-full aspect-square border-2 transition-colors',
                      currentImageIndex === i
                        ? 'border-neutral-900'
                        : 'border-transparent hover:border-neutral-300',
                    )}
                  >
                    <Image
                      src={buildImageSrc(image, 150)}
                      alt={image.accessibilityLabel}
                      className="block w-full h-full object-cover"
                      height={88}
                      width={88}
                    />
                  </button>
                ))}
              </div>
            </div>

            {(canScrollPrev || canScrollNext) && (
              <button
                onClick={() => thumbsApi?.scrollNext()}
                aria-label={t('productDetailPageContent.nextImagesText', undefined, {
                  default: 'Next images',
                })}
                tabIndex={canScrollNext ? undefined : -1}
                className={classNames(
                  'shrink-0 flex items-center justify-center w-6 h-6 text-neutral-600 hover:text-neutral-900 border-2 border-neutral-300 transition-opacity duration-200',
                  !canScrollNext && 'opacity-0 pointer-events-none',
                )}
              >
                <ChevronIcon direction={isDesktop ? 'down' : 'right'} className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="order-1 lg:order-2 flex-1 min-w-0">
          {activeImage && (
            <Image
              src={buildImageSrc(activeImage, 800)}
              alt={activeImage.accessibilityLabel}
              className="block w-full aspect-[800/600] object-cover"
              height={600}
              width={800}
              priority
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MeaterPDPHeroCarousel;
